/**
 * Eligibility Result Card
 * Formatted display of eligibility verification result.
 */

import React from 'react';
import { EligibilityResult } from '@/services/api/eligibility';

interface Props {
  result: EligibilityResult;
}

const EligibilityResultCard: React.FC<Props> = ({ result }) => {
  const statusConfig: Record<string, { bg: string; text: string; label: string }> = {
    ACTIVE: { bg: 'bg-green-50 border-green-200', text: 'text-green-700', label: 'Active' },
    INACTIVE: { bg: 'bg-red-50 border-red-200', text: 'text-red-700', label: 'Inactive' },
    ESTIMATED: { bg: 'bg-yellow-50 border-yellow-200', text: 'text-yellow-700', label: 'Estimated' },
    PENDING: { bg: 'bg-blue-50 border-blue-200', text: 'text-blue-700', label: 'Pending' },
    ERROR: { bg: 'bg-red-50 border-red-200', text: 'text-red-700', label: 'Error' },
    UNKNOWN: { bg: 'bg-gray-50 border-gray-200', text: 'text-gray-700', label: 'Unknown' },
  };

  const config = statusConfig[result.eligibility_status] || statusConfig.UNKNOWN;

  const scopeLabel: Record<string, string> = {
    FULL_SCOPE: 'Full-Scope Medi-Cal',
    RESTRICTED: 'Restricted Scope',
    EMERGENCY_ONLY: 'Emergency Services Only',
  };

  return (
    <div className={`rounded-lg border-2 p-4 ${config.bg}`}>
      {/* Status Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className={`text-lg font-bold ${config.text}`}>
            {result.is_eligible ? 'Eligible' : result.eligibility_status === 'ESTIMATED' ? 'Likely Eligible' : 'Not Eligible'}
          </span>
          <span className={`text-xs px-2 py-0.5 rounded-full border ${config.bg} ${config.text}`}>
            {config.label}
          </span>
        </div>
        {result.from_cache && (
          <span className="text-xs text-gray-400">Cached result</span>
        )}
        {result.response_time_ms && (
          <span className="text-xs text-gray-400">{result.response_time_ms}ms</span>
        )}
      </div>

      {/* Main Info Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {result.mcp_plan_name && (
          <InfoField label="Managed Care Plan" value={result.mcp_plan_name} highlight />
        )}
        {result.benefits_scope && (
          <InfoField label="Benefits Scope" value={scopeLabel[result.benefits_scope] || result.benefits_scope} />
        )}
        {result.aid_code && (
          <InfoField label="Aid Code" value={result.aid_code} />
        )}
        {result.county_name && (
          <InfoField label="County" value={result.county_name} />
        )}
        {result.effective_date && (
          <InfoField label="Effective Date" value={result.effective_date} />
        )}
        {result.share_of_cost !== null && result.share_of_cost !== undefined && (
          <InfoField label="Share of Cost" value={`$${result.share_of_cost.toFixed(2)}`} />
        )}
        {result.evc_number && (
          <InfoField label="EVC Number" value={result.evc_number} />
        )}
        {result.query_channel && (
          <InfoField label="Verification Channel" value={result.query_channel.replace(/_/g, ' ')} />
        )}
        {result.mcp_model_type && (
          <InfoField label="MCP Model" value={result.mcp_model_type} />
        )}
      </div>

      {/* Other Coverage */}
      {result.other_health_coverage && result.other_health_coverage.length > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <span className="text-xs font-medium text-gray-500">Other Coverage: </span>
          {result.other_health_coverage.map((c: any, i: number) => (
            <span key={i} className="text-xs bg-gray-100 px-2 py-0.5 rounded mr-1">
              {c.coverage_type || c}
            </span>
          ))}
        </div>
      )}

      {/* Pre-screen info */}
      {result.pre_screen && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <span className="text-xs font-medium text-gray-500">
            FPL: {result.pre_screen.fpl_percentage}% |
            Programs: {result.pre_screen.eligible_programs.map((p: any) => p.program_name).join(', ')}
          </span>
        </div>
      )}

      {/* Errors */}
      {result.errors && result.errors.length > 0 && (
        <div className="mt-3 p-2 bg-red-100 rounded text-xs text-red-700">
          {result.errors.join('; ')}
        </div>
      )}
    </div>
  );
};

const InfoField: React.FC<{ label: string; value: string; highlight?: boolean }> = ({
  label,
  value,
  highlight,
}) => (
  <div>
    <div className="text-xs text-gray-500">{label}</div>
    <div className={`text-sm ${highlight ? 'font-semibold' : ''}`}>{value}</div>
  </div>
);

export default EligibilityResultCard;
